import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../../../components/ui/alert-dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { MapPin, Plus, Pencil, Trash2, Check, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export const EditLocationsModal = ({ open, onOpenChange, projectId, onLocationsChanged, apiBase = '/electrical-systems' }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, usage }
  const [deletingId, setDeletingId] = useState(null);

  const fetchLocations = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}${apiBase}/${projectId}/locations`);
      setLocations(res.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [projectId, apiBase]);

  useEffect(() => { if (open) fetchLocations(); }, [open, fetchLocations]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}${apiBase}/${projectId}/locations`, { name });
      if (res.data.message === 'Location already exists') {
        toast.info(`"${res.data.name}" already exists`);
      } else {
        toast.success(`"${name}" added`);
      }
      setNewName('');
      await fetchLocations();
      onLocationsChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add location');
    }
    setAdding(false);
  };

  const startEdit = (loc) => { setEditingId(loc.id); setEditName(loc.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };

  const handleRename = async (id) => {
    const name = editName.trim();
    if (!name) return;
    setSavingId(id);
    try {
      const res = await axios.put(`${API}${apiBase}/${projectId}/locations/${id}`, { name });
      const count = res.data.cascadedRecords || 0;
      toast.success(`Renamed. ${count > 0 ? `${count} record(s) updated.` : ''}`);
      setEditingId(null);
      await fetchLocations();
      onLocationsChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Rename failed';
      toast.error(detail);
    }
    setSavingId(null);
  };

  const requestDelete = async (loc) => {
    try {
      const res = await axios.get(`${API}${apiBase}/${projectId}/locations/${loc.id}/usage`);
      setDeleteConfirm({ ...loc, usage: res.data });
    } catch {
      setDeleteConfirm({ ...loc, usage: { totalUsage: -1 } });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeletingId(deleteConfirm.id);
    try {
      await axios.delete(`${API}${apiBase}/${projectId}/locations/${deleteConfirm.id}`);
      toast.success(`"${deleteConfirm.name}" deleted`);
      setDeleteConfirm(null);
      await fetchLocations();
      onLocationsChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
    setDeletingId(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="edit-locations-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />Edit Locations
            </DialogTitle>
          </DialogHeader>

          {/* Add new */}
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="New location name..."
              className="h-9 text-sm"
              data-testid="new-location-input"
            />
            <Button size="sm" onClick={handleAdd} disabled={adding || !newName.trim()} className="shrink-0" data-testid="add-location-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>

          {/* List */}
          <div className="max-h-64 overflow-y-auto border rounded-md divide-y" data-testid="locations-edit-list">
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : locations.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">No locations</div>
            ) : locations.map(loc => (
              <div key={loc.id} className="flex items-center gap-2 px-3 py-2 group hover:bg-slate-50" data-testid={`loc-row-${loc.id}`}>
                {editingId === loc.id ? (
                  <>
                    <Input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(loc.id); if (e.key === 'Escape') cancelEdit(); }}
                      className="h-7 text-sm flex-1"
                      autoFocus
                      data-testid="rename-input"
                    />
                    <button onClick={() => handleRename(loc.id)} disabled={savingId === loc.id} className="text-green-600 hover:text-green-700 p-1" data-testid="rename-save">
                      {savingId === loc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-600 p-1" data-testid="rename-cancel">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <MapPin className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                    <span className="text-sm text-slate-700 flex-1">{loc.name}</span>
                    <button onClick={() => startEdit(loc)} className="text-slate-400 hover:text-blue-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-loc-${loc.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => requestDelete(loc)} className="text-slate-400 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-loc-${loc.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          <p className="text-[10px] text-slate-400">{locations.length} location{locations.length !== 1 ? 's' : ''}</p>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => { if (!v) setDeleteConfirm(null); }}>
        <AlertDialogContent data-testid="delete-location-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />Delete Location
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {deleteConfirm?.usage?.totalUsage > 0 ? (
                  <div className="space-y-2">
                    <p><strong>&quot;{deleteConfirm?.name}&quot;</strong> cannot be deleted because it is used by:</p>
                    <ul className="text-sm space-y-1 ml-4 list-disc">
                      {deleteConfirm.usage.panels?.length > 0 && <li>{deleteConfirm.usage.panels.length} panel(s): {deleteConfirm.usage.panels.map(p => p.name).join(', ')}</li>}
                      {deleteConfirm.usage.equipment?.length > 0 && <li>{deleteConfirm.usage.equipment.length} equipment: {deleteConfirm.usage.equipment.map(e => e.tag).join(', ')}</li>}
                      {deleteConfirm.usage.devices?.length > 0 && <li>{deleteConfirm.usage.devices.length} device(s)</li>}
                      {deleteConfirm.usage.circuits?.length > 0 && <li>{deleteConfirm.usage.circuits.length} circuit(s)</li>}
                    </ul>
                    <p className="text-xs text-slate-500">Remove the location from these records first, then try again.</p>
                  </div>
                ) : (
                  <p>Are you sure you want to delete <strong>&quot;{deleteConfirm?.name}&quot;</strong>? This action cannot be undone.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">{deleteConfirm?.usage?.totalUsage > 0 ? 'Ok' : 'Cancel'}</AlertDialogCancel>
            {deleteConfirm?.usage?.totalUsage === 0 && (
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">
                {deletingId ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
